/**
 * Controlli e form di editing delle sezioni ripetibili del CV Europass, usati
 * dall'Editable_Mode della vista condivisa (`EuropassCvViewer`).
 *
 * Esporta:
 * - {@link SectionEditButton} — pulsante "Modifica" a destra del titolo di sezione;
 * - {@link ItemEditControls} — pulsanti Modifica/Rimuovi di un singolo item;
 * - {@link SectionItemModal} — dialog con il form di aggiunta/modifica di un
 *   item, con validazione client-side via `validateSectionItem`.
 *
 * Tutti i controlli esistono solo in Editable_Mode: i consumatori li
 * renderizzano unicamente quando `editable` è `true` e sono passati i relativi
 * callback. In modalità read-only (flusso CV esterni) nessuno di questi
 * componenti viene montato, così il DOM e il comportamento restano identici.
 */
import { useState, type FormEvent } from 'react';
import {
  SECTION_LABELS,
  validateSectionItem,
  validateSingleSection,
  type RepeatableSection,
  type SectionItemByName,
  type SingleSection,
} from './europassCvSectionSchemas';
import { Alert, Button, Field, Icon, Modal, TextArea, TextInput } from './ui';

/** Pulsanti Modifica/Rimuovi di un singolo item di una sezione ripetibile. */
export function ItemEditControls({
  onEdit,
  onRemove,
  busy = false,
  itemLabel,
}: {
  onEdit: () => void;
  onRemove: () => void;
  busy?: boolean;
  /** Etichetta accessibile dell'item (es. "esperienza lavorativa"). */
  itemLabel: string;
}) {
  return (
    <div className="europass-cv__item-actions">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        iconOnly
        aria-label={`Modifica ${itemLabel}`}
        onClick={onEdit}
        disabled={busy}
      >
        <Icon name="edit" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        iconOnly
        aria-label={`Rimuovi ${itemLabel}`}
        onClick={onRemove}
        loading={busy}
        disabled={busy}
      >
        <Icon name="trash" />
      </Button>
    </div>
  );
}

/**
 * Altezza (in righe) delle textarea dei form: abbondante spazio verticale per
 * le descrizioni, come richiesto dal layout.
 */
const TEXTAREA_ROWS = 5;

/** Definizione di un campo del form di una sezione. */
interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'checkbox' | 'tags';
  /**
   * Se `true`, il campo occupa l'intera larghezza della griglia (entrambe le
   * colonne). Le `textarea` e i `tags` sono sempre a tutta larghezza a
   * prescindere da questo flag (vedi `isFullWidthField`).
   */
  fullWidth?: boolean;
}

/**
 * Campi del form per ciascuna sezione ripetibile (chiavi in inglese).
 *
 * L'ordine determina il flusso nella griglia a due colonne: i campi sono
 * raggruppati per coerenza (es. città/paese, date di inizio/fine) e le checkbox
 * sono affiancate a un campo correlato invece di restare da sole su una riga
 * (es. "In corso" accanto alle date, "Madrelingua" accanto alla lingua).
 * Descrizioni e tecnologie occupano l'intera larghezza.
 */
const SECTION_FIELDS: Record<RepeatableSection, FieldDef[]> = {
  workExperience: [
    { name: 'jobTitle', label: 'Ruolo', type: 'text' },
    { name: 'employer', label: 'Datore di lavoro', type: 'text' },
    { name: 'city', label: 'Città', type: 'text' },
    { name: 'country', label: 'Paese', type: 'text' },
    { name: 'startDate', label: 'Dal', type: 'text' },
    { name: 'endDate', label: 'Al', type: 'text' },
    // "In corso" affiancata a "Settore" così non resta sola su una riga.
    { name: 'current', label: 'In corso', type: 'checkbox' },
    { name: 'sector', label: 'Settore', type: 'text' },
    { name: 'description', label: 'Descrizione', type: 'textarea' },
  ],
  education: [
    { name: 'qualification', label: 'Titolo', type: 'text' },
    { name: 'institution', label: 'Istituto', type: 'text' },
    { name: 'city', label: 'Città', type: 'text' },
    { name: 'country', label: 'Paese', type: 'text' },
    { name: 'startDate', label: 'Dal', type: 'text' },
    { name: 'endDate', label: 'Al', type: 'text' },
    // "In corso" affiancata a "Livello" così non resta sola su una riga.
    { name: 'current', label: 'In corso', type: 'checkbox' },
    { name: 'level', label: 'Livello', type: 'text' },
    { name: 'description', label: 'Descrizione', type: 'textarea' },
  ],
  languages: [
    { name: 'language', label: 'Lingua', type: 'text' },
    // "Madrelingua" affiancata alla lingua: coppia logica, nessuna riga vuota.
    { name: 'mother', label: 'Madrelingua', type: 'checkbox' },
    { name: 'overall', label: 'Livello complessivo', type: 'text' },
    { name: 'listening', label: 'Ascolto', type: 'text' },
    { name: 'reading', label: 'Lettura', type: 'text' },
    { name: 'spokenInteraction', label: 'Interazione orale', type: 'text' },
    { name: 'spokenProduction', label: 'Produzione orale', type: 'text' },
    { name: 'writing', label: 'Scrittura', type: 'text' },
  ],
  projects: [
    { name: 'name', label: 'Nome', type: 'text', fullWidth: true },
    { name: 'startDate', label: 'Dal', type: 'text' },
    { name: 'endDate', label: 'Al', type: 'text' },
    { name: 'technologies', label: 'Tecnologie (separate da virgola)', type: 'tags' },
    { name: 'description', label: 'Descrizione', type: 'textarea' },
  ],
  awards: [
    { name: 'title', label: 'Titolo', type: 'text', fullWidth: true },
    { name: 'issuer', label: 'Ente', type: 'text' },
    { name: 'date', label: 'Data', type: 'text' },
    { name: 'description', label: 'Descrizione', type: 'textarea' },
  ],
  publications: [
    { name: 'title', label: 'Titolo', type: 'text' },
    { name: 'publisher', label: 'Editore', type: 'text' },
    { name: 'date', label: 'Data', type: 'text' },
    { name: 'reference', label: 'Riferimento', type: 'text' },
  ],
};

/**
 * I campi di tipo `textarea` e `tags` occupano sempre l'intera larghezza della
 * griglia; gli altri rispettano il flag `fullWidth`. Centralizza la regola così
 * i due form (ripetibile e singolo) restano coerenti.
 */
function isFullWidthField(type: FieldDef['type'] | SingleFieldDef['type']): boolean {
  return type === 'textarea' || type === 'tags' || type === 'links';
}

/** Stato grezzo del form: valori per nome campo. */
type FormState = Record<string, string | boolean>;

/** Costruisce lo stato iniziale del form da un item esistente (o vuoto). */
function initialFormState(
  section: RepeatableSection,
  item: Readonly<Record<string, unknown>> | undefined,
): FormState {
  const state: FormState = {};
  for (const field of SECTION_FIELDS[section]) {
    const value = item?.[field.name];
    if (field.type === 'checkbox') {
      state[field.name] = value === true;
    } else if (field.type === 'tags') {
      state[field.name] = Array.isArray(value) ? value.join(', ') : '';
    } else {
      state[field.name] = typeof value === 'string' ? value : '';
    }
  }
  return state;
}

/** Converte lo stato del form in input grezzo per il validatore. */
function formStateToInput(
  section: RepeatableSection,
  state: FormState,
): Record<string, unknown> {
  const input: Record<string, unknown> = {};
  for (const field of SECTION_FIELDS[section]) {
    const value = state[field.name];
    if (field.type === 'tags') {
      input[field.name] = String(value ?? '')
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t !== '');
    } else {
      input[field.name] = value;
    }
  }
  return input;
}

/**
 * Dialog con il form di aggiunta o modifica di un item di una sezione
 * ripetibile. Valida con `validateSectionItem` (stesso contratto dello schema
 * zod di sezione lato API) prima di invocare `onSubmit` con l'item normalizzato.
 */
export function SectionItemModal<S extends RepeatableSection>({
  section,
  mode,
  initialItem,
  onSubmit,
  onClose,
}: {
  section: S;
  mode: 'add' | 'edit';
  /** Item corrente in modalità modifica (assente in aggiunta). */
  initialItem?: SectionItemByName[S];
  /** Persiste l'item normalizzato; può essere asincrono. */
  onSubmit: (item: SectionItemByName[S]) => void | Promise<void>;
  onClose: () => void;
}) {
  const [state, setState] = useState<FormState>(() =>
    initialFormState(section, initialItem as Readonly<Record<string, unknown>> | undefined),
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const sectionLabel = SECTION_LABELS[section];
  const title =
    mode === 'add' ? `Aggiungi ${sectionLabel}` : `Modifica ${sectionLabel}`;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const result = validateSectionItem(section, formStateToInput(section, state));
    if (!result.success) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      await onSubmit(result.data);
      onClose();
    } catch {
      setErrors(['Salvataggio non riuscito. Riprova.']);
    } finally {
      setSubmitting(false);
    }
  };

  const setValue = (name: string, value: string | boolean) =>
    setState((prev) => ({ ...prev, [name]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annulla
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="europass-section-item-form"
            loading={submitting}
            disabled={submitting}
          >
            Salva
          </Button>
        </>
      }
    >
      <form id="europass-section-item-form" onSubmit={handleSubmit} noValidate>
        {errors.length > 0 && (
          <Alert tone="error">
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="europass-cv__form-errors">
                {errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <div className="europass-cv__form-grid">
          {SECTION_FIELDS[section].map((field) => {
            const fieldId = `europass-field-${section}-${field.name}`;
            const fullWidth = field.fullWidth === true || isFullWidthField(field.type);
            if (field.type === 'checkbox') {
              return (
                <label key={field.name} className="europass-cv__form-checkbox" htmlFor={fieldId}>
                  <input
                    id={fieldId}
                    type="checkbox"
                    checked={state[field.name] === true}
                    onChange={(e) => setValue(field.name, e.target.checked)}
                  />
                  {field.label}
                </label>
              );
            }
            return (
              <div
                key={field.name}
                className={fullWidth ? 'europass-cv__form-cell--full' : undefined}
              >
                <Field id={fieldId} label={field.label}>
                  {field.type === 'textarea' ? (
                    <TextArea
                      id={fieldId}
                      rows={TEXTAREA_ROWS}
                      value={String(state[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                    />
                  ) : (
                    <TextInput
                      id={fieldId}
                      value={String(state[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                    />
                  )}
                </Field>
              </div>
            );
          })}
        </div>
      </form>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Editor delle sezioni singole (dati personali, profilo, competenze)
// ---------------------------------------------------------------------------

/**
 * Pulsante "Modifica" di una sezione, pensato per stare a destra del titolo
 * (nell'header di sezione o di card). È il solo `Button`: il posizionamento è
 * demandato al contenitore (`.europass-cv__section-heading-action`).
 */
export function SectionEditButton({
  label,
  onClick,
  busy = false,
}: {
  label: string;
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <Button type="button" size="sm" variant="ghost" onClick={onClick} disabled={busy}>
      <Icon name="edit" />
      {label}
    </Button>
  );
}

/** Definizione di un campo del form di una sezione singola. */
interface SingleFieldDef {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'links';
  /** Se `true`, il campo occupa l'intera larghezza (entrambe le colonne). */
  fullWidth?: boolean;
}

/**
 * Campi del form per ciascuna sezione singola (chiavi in inglese).
 *
 * `personalInfo` è raggruppato per coerenza nelle due colonne: identità
 * (nome/cognome), contatti (email/telefono), indirizzo (via a tutta larghezza,
 * poi città/CAP, regione/paese), dati anagrafici (nascita/nazionalità, genere)
 * e infine i link a tutta larghezza. `summary` e `skills` sono sole textarea
 * (a tutta larghezza, con ampio spazio verticale).
 */
const SINGLE_SECTION_FIELDS: Record<SingleSection, SingleFieldDef[]> = {
  personalInfo: [
    { name: 'firstName', label: 'Nome', type: 'text' },
    { name: 'lastName', label: 'Cognome', type: 'text' },
    { name: 'headline', label: 'Titolo professionale', type: 'text', fullWidth: true },
    { name: 'email', label: 'Email', type: 'text' },
    { name: 'phone', label: 'Telefono', type: 'text' },
    { name: 'street', label: 'Via/Indirizzo', type: 'text', fullWidth: true },
    { name: 'city', label: 'Città', type: 'text' },
    { name: 'postalCode', label: 'CAP', type: 'text' },
    { name: 'region', label: 'Regione/Provincia', type: 'text' },
    { name: 'country', label: 'Paese', type: 'text' },
    { name: 'dateOfBirth', label: 'Data di nascita', type: 'text' },
    { name: 'nationality', label: 'Nazionalità', type: 'text' },
    { name: 'gender', label: 'Genere', type: 'text' },
    { name: 'links', label: 'Profili/siti web (uno per riga)', type: 'links' },
  ],
  summary: [
    { name: 'summary', label: 'Profilo professionale', type: 'textarea' },
  ],
  skills: [
    { name: 'communication', label: 'Competenze comunicative', type: 'textarea' },
    { name: 'organisational', label: 'Competenze organizzative e gestionali', type: 'textarea' },
    { name: 'jobRelated', label: 'Competenze professionali', type: 'textarea' },
    { name: 'digital', label: 'Competenze digitali', type: 'textarea' },
    { name: 'other', label: 'Altre competenze', type: 'textarea' },
  ],
};

/** Titolo del modal per ciascuna sezione singola. */
const SINGLE_SECTION_TITLE: Record<SingleSection, string> = {
  personalInfo: 'Modifica dati personali',
  summary: 'Modifica profilo professionale',
  skills: 'Modifica competenze',
};

/** Costruisce lo stato iniziale del form da un valore corrente della sezione. */
function initialSingleFormState(
  section: SingleSection,
  current: Readonly<Record<string, unknown>> | undefined,
): Record<string, string> {
  const state: Record<string, string> = {};
  for (const field of SINGLE_SECTION_FIELDS[section]) {
    // Per i dati personali l'indirizzo è annidato in `address`: appiattito nel form.
    const addressKeys = new Set(['street', 'city', 'postalCode', 'region', 'country']);
    let value: unknown;
    if (section === 'personalInfo' && addressKeys.has(field.name)) {
      const address = (current?.address ?? {}) as Record<string, unknown>;
      value = address[field.name];
    } else {
      value = current?.[field.name];
    }
    if (field.type === 'links') {
      state[field.name] = Array.isArray(value) ? value.join('\n') : '';
    } else {
      state[field.name] = typeof value === 'string' ? value : '';
    }
  }
  return state;
}

/**
 * Dialog con il form di modifica di una SEZIONE SINGOLA (dati personali,
 * profilo professionale, competenze). Valida con `validateSingleSection` e
 * invoca `onSubmit` con il dato normalizzato pronto per `onSectionUpdate`.
 */
export function SingleSectionModal({
  section,
  current,
  onSubmit,
  onClose,
}: {
  section: SingleSection;
  /** Valore corrente della sezione (oggetto per personalInfo/skills, stringa per summary). */
  current: unknown;
  /** Persiste il dato normalizzato della sezione; può essere asincrono. */
  onSubmit: (data: unknown) => void | Promise<void>;
  onClose: () => void;
}) {
  // `summary` è una stringa: la normalizziamo in `{ summary }` per il form.
  const currentRecord =
    section === 'summary'
      ? { summary: typeof current === 'string' ? current : '' }
      : ((current ?? {}) as Record<string, unknown>);

  const [state, setState] = useState<Record<string, string>>(() =>
    initialSingleFormState(section, currentRecord),
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const result = validateSingleSection(section, state);
    if (!result.success) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      // Per `summary` inviamo la sola stringa come dato della sezione.
      const data =
        section === 'summary'
          ? ((result.data as { summary: string }).summary ?? '')
          : result.data;
      await onSubmit(data);
      onClose();
    } catch {
      setErrors(['Salvataggio non riuscito. Riprova.']);
    } finally {
      setSubmitting(false);
    }
  };

  const setValue = (name: string, value: string) =>
    setState((prev) => ({ ...prev, [name]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={SINGLE_SECTION_TITLE[section]}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annulla
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="europass-single-section-form"
            loading={submitting}
            disabled={submitting}
          >
            Salva
          </Button>
        </>
      }
    >
      <form id="europass-single-section-form" onSubmit={handleSubmit} noValidate>
        {errors.length > 0 && (
          <Alert tone="error">
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="europass-cv__form-errors">
                {errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <div className="europass-cv__form-grid">
          {SINGLE_SECTION_FIELDS[section].map((field) => {
            const fieldId = `europass-single-${section}-${field.name}`;
            const fullWidth = field.fullWidth === true || isFullWidthField(field.type);
            return (
              <div
                key={field.name}
                className={fullWidth ? 'europass-cv__form-cell--full' : undefined}
              >
                <Field id={fieldId} label={field.label}>
                  {field.type === 'text' ? (
                    <TextInput
                      id={fieldId}
                      value={state[field.name] ?? ''}
                      onChange={(e) => setValue(field.name, e.target.value)}
                    />
                  ) : (
                    // `textarea` e `links` (una URL per riga): ampio spazio verticale.
                    <TextArea
                      id={fieldId}
                      rows={TEXTAREA_ROWS}
                      value={state[field.name] ?? ''}
                      onChange={(e) => setValue(field.name, e.target.value)}
                    />
                  )}
                </Field>
              </div>
            );
          })}
        </div>
      </form>
    </Modal>
  );
}
